import React, { useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import { getStaticFileUrl, guessOtaStaticPath, resolveChannelImageUrl } from '../../utils/staticUtils';

/**
 * Component to render channel image with fallback to Material Design icon
 */
const ChannelImageWithFallback = ({ channel, size = 24 }) => {
  const [imageError, setImageError] = useState(false);
  const [useNameFallback, setUseNameFallback] = useState(false);

  const primarySrc = resolveChannelImageUrl(channel);
  const guessed = guessOtaStaticPath(channel?.name);
  const fallbackSrc = guessed ? getStaticFileUrl(guessed) : null;
  const imageSrc = useNameFallback ? fallbackSrc : primarySrc;

  if (imageError || !imageSrc) {
    return (
      <Avatar
        sx={{
          width: size,
          height: size,
          bgcolor: 'action.disabledBackground',
          color: 'action.disabled',
        }}
      >
        <ImageIcon sx={{ fontSize: size * 0.6 }} />
      </Avatar>
    );
  }

  return (
    <Box
      component="img"
      src={imageSrc}
      alt={channel.name}
      sx={{
        width: size,
        height: size,
        objectFit: 'contain',
        flexShrink: 0,
      }}
      onError={() => {
        if (!useNameFallback && fallbackSrc && fallbackSrc !== imageSrc) {
          setUseNameFallback(true);
          return;
        }
        setImageError(true);
      }}
    />
  );
};

/**
 * Component to render PMS image with fallback to Material Design icon
 */
const PMSImageWithFallback = ({ size = 24 }) => {
  const [imageError, setImageError] = useState(false);
  
  if (imageError) {
    return (
      <Avatar
        sx={{
          width: size,
          height: size,
          bgcolor: 'action.disabledBackground',
          color: 'action.disabled',
        }}
      >
        <ImageIcon sx={{ fontSize: size * 0.6 }} />
      </Avatar>
    );
  }
  
  return (
    <Box
      component="img"
      src={getStaticFileUrl('ota/pms.png')}
      alt="PMS"
      sx={{
        width: size,
        height: size,
        objectFit: 'contain',
        flexShrink: 0,
      }}
      onError={() => {
        setImageError(true);
      }}
    />
  );
};

/**
 * ChannelSelect - Material Design dropdown with channel images
 * 
 * @param {Object} props
 * @param {string} value - Selected channel value ('all', 'pms', or channel ID)
 * @param {Function} onChange - Callback when selection changes
 * @param {Array} channels - Array of channel objects with {id, name, image_path}
 * @param {string} label - Label for the select
 * @param {boolean} disabled - Whether the select is disabled
 * @param {Object} sx - Additional sx styles
 * @param {boolean} includeAll - Whether to include "All Channels" option
 * @param {boolean} includePMS - Whether to include "PMS" option
 * @param {string} allLabel - Label for the all-channels option (e.g. "OTA" on mobile)
 */
const ChannelSelect = ({
  value,
  onChange,
  channels = [],
  label = 'Channel',
  disabled = false,
  sx = {},
  includeAll = true,
  includePMS = true,
  size = 'small',
  allLabel = 'All Channels',
}) => {
  const renderValue = (selected) => {
    if (selected === 'all') {
      return includeAll ? allLabel : 'PMS';
    }
    if (selected === 'pms') {
      return 'PMS';
    }
    const channel = channels.find(c => c.id.toString() === selected);
    if (channel) {
      // Only return the channel name, not the full path or image
      return channel.name || '';
    }
    return '';
  };


  return (
    <FormControl size={size} sx={{ width: '100%', ...sx }}>
      <InputLabel sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={onChange}
        disabled={disabled}
        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        renderValue={renderValue}
      >
        {includeAll && (
          <MenuItem value="all">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">{allLabel}</Typography>
            </Box>
          </MenuItem>
        )}
        {includePMS && (
          <MenuItem value="pms">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                }}
              >
                <PMSImageWithFallback size={24} />
              </Box>
              <Typography variant="body2">PMS</Typography>
            </Box>
          </MenuItem>
        )}
        {channels.map((channel) => (
          <MenuItem key={channel.id} value={channel.id.toString()}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                }}
              >
                <ChannelImageWithFallback channel={channel} size={24} />
              </Box>
              <Typography variant="body2">{channel.name}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ChannelSelect;
